import { useState, useEffect, useRef } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardFooter } from "@/components/ui/card";
import { GraduationCap, Loader2, CheckCircle2, AlertCircle } from "lucide-react";
import { useBranding } from "@/context/BrandingContext";
import { useAuth } from "@/context/AuthContext";
import { authService } from "@/services/authService";
import { SearchablePicklist } from "@/components/ui/searchable-picklist";
import { PhoneCodePicker } from "@/components/ui/phone-code-picker";
import { parseThrottleSeconds } from "@/utils/helpers";

type Step = "form" | "verification-sent" | "already-registered";

interface LocationState {
    verifyEmail?: string;
    expiresIn?: number | null;
}

const CONTACT_METHODS = ["Call / SMS", "WhatsApp", "Email"];

const AgencyRegister = () => {
    const { branding, loading } = useBranding();
    const { setUser } = useAuth();
    const navigate = useNavigate();
    const location = useLocation();
    const locationState = location.state as LocationState | null;

    const [step, setStep] = useState<Step>(() =>
        locationState?.verifyEmail ? "verification-sent" : "form"
    );
    const [isPending, setIsPending] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [countries, setCountries] = useState<string[]>([]);

    const [formData, setFormData] = useState({
        company_name: "",
        tax_number: "",
        website: "",
        address_country: "",
        address_city: "",
        address_street: "",
        address_zip: "",
        first_name: "",
        last_name: "",
        preferred_contact_method: "",
        phone_code: "+90",
        phone_number: "",
        email: locationState?.verifyEmail || "",
        password: "",
        confirm_password: "",
    });

    const [verificationCode, setVerificationCode] = useState("");
    const [timeLeft, setTimeLeft] = useState(900);
    const [canResend, setCanResend] = useState(false);
    const [isResending, setIsResending] = useState(false);
    const [timerKey, setTimerKey] = useState(0);
    const [throttleCooldown, setThrottleCooldown] = useState(0);
    const [fieldErrors, setFieldErrors] = useState({
        company_name: null as string | null,
        first_name: null as string | null,
        last_name: null as string | null,
        phone_number: null as string | null,
        email: null as string | null,
        password: null as string | null,
        confirm_password: null as string | null,
    });

    const initialTimeLeftRef = useRef<number | null>(
        locationState?.verifyEmail
            ? locationState.expiresIn && locationState.expiresIn > 0
                ? locationState.expiresIn
                : 0
            : null
    );

    const companyNameRef = useRef<HTMLInputElement>(null);
    const firstNameRef = useRef<HTMLInputElement>(null);
    const lastNameRef = useRef<HTMLInputElement>(null);
    const phoneNumberRef = useRef<HTMLInputElement>(null);
    const emailRef = useRef<HTMLInputElement>(null);
    const passwordRef = useRef<HTMLInputElement>(null);
    const confirmPasswordRef = useRef<HTMLInputElement>(null);

    const primaryColor = branding?.branding?.colors?.primary || "#1d3658";

    useEffect(() => {
        if (locationState?.verifyEmail) {
            navigate(location.pathname, { replace: true, state: null });
        }
    }, []);

    useEffect(() => {
        fetch("/api/integrations/hub/countries/")
            .then((r) => r.json())
            .then((data) => {
                if (data.status === "success" && data.countries?.length > 0) {
                    setCountries(data.countries);
                }
            })
            .catch(() => {});
    }, []);

    useEffect(() => {
        if (step !== "verification-sent") return;
        const startTime = initialTimeLeftRef.current ?? 900;
        initialTimeLeftRef.current = null;
        setTimeLeft(startTime);
        setCanResend(startTime <= 0);
        if (startTime <= 0) return;
        const timer = setInterval(() => {
            setTimeLeft((prev) => {
                if (prev <= 1) {
                    setCanResend(true);
                    clearInterval(timer);
                    return 0;
                }
                return prev - 1;
            });
        }, 1000);
        return () => clearInterval(timer);
    }, [step, timerKey]);

    useEffect(() => {
        if (throttleCooldown <= 0) return;
        const t = window.setTimeout(() => setThrottleCooldown((p) => Math.max(0, p - 1)), 1000);
        return () => window.clearTimeout(t);
    }, [throttleCooldown]);

    const set = (field: string, value: string) =>
        setFormData((prev) => ({ ...prev, [field]: value }));

    const handleNameChange = (field: "first_name" | "last_name", value: string) => {
        const normalized = value.normalize("NFC");
        const hasInvalidChars = /[^\p{L}\s\-\u2019']/u.test(normalized);
        const cleaned = normalized.replace(/[^\p{L}\s\-\u2019']/gu, "");
        const codePoints = Array.from(cleaned);
        const isTruncated = codePoints.length > 40;
        const sanitized = codePoints.slice(0, 40).join("");

        if (hasInvalidChars) {
            setFieldErrors((prev) => ({ ...prev, [field]: "Only letters, spaces, hyphens, and apostrophes are allowed." }));
        } else if (isTruncated) {
            setFieldErrors((prev) => ({ ...prev, [field]: "Maximum 40 characters allowed." }));
        } else {
            setFieldErrors((prev) => ({ ...prev, [field]: null }));
        }
        setFormData((prev) => ({ ...prev, [field]: sanitized }));
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setError(null);

        const errors = {
            company_name: !formData.company_name.trim() ? "Company name is required." : null,
            first_name: !formData.first_name.trim() ? "First name is required." : null,
            last_name: !formData.last_name.trim() ? "Last name is required." : null,
            phone_number: !formData.phone_number.trim() ? "Phone number is required." : null,
            email: !formData.email.trim() ? "Email is required." : null,
            password: !formData.password
                ? "Password is required."
                : formData.password.length < 8
                ? "Password must be at least 8 characters."
                : null,
            confirm_password: !formData.confirm_password
                ? "Please confirm your password."
                : formData.password !== formData.confirm_password
                ? "Passwords do not match."
                : null,
        };

        const hasErrors = Object.values(errors).some(Boolean);
        if (hasErrors) {
            setFieldErrors(errors);
            if (errors.company_name) companyNameRef.current?.scrollIntoView({ behavior: "smooth", block: "center" });
            else if (errors.first_name) firstNameRef.current?.scrollIntoView({ behavior: "smooth", block: "center" });
            else if (errors.last_name) lastNameRef.current?.scrollIntoView({ behavior: "smooth", block: "center" });
            else if (errors.phone_number) phoneNumberRef.current?.scrollIntoView({ behavior: "smooth", block: "center" });
            else if (errors.email) emailRef.current?.scrollIntoView({ behavior: "smooth", block: "center" });
            else if (errors.password) passwordRef.current?.scrollIntoView({ behavior: "smooth", block: "center" });
            else if (errors.confirm_password) confirmPasswordRef.current?.scrollIntoView({ behavior: "smooth", block: "center" });
            return;
        }

        setIsPending(true);
        try {
            await authService.agencyRegister({
                company_name: formData.company_name,
                tax_number: formData.tax_number,
                website: formData.website,
                address_country: formData.address_country,
                address_city: formData.address_city,
                address_street: formData.address_street,
                address_zip: formData.address_zip,
                first_name: formData.first_name,
                last_name: formData.last_name,
                preferred_contact_method: formData.preferred_contact_method,
                phone_code: formData.phone_code,
                phone_number: formData.phone_number,
                email: formData.email,
                password: formData.password,
            });
            setStep("verification-sent");
        } catch (err: any) {
            const data = err.response?.data;
            if (data?.error === "already_registered") {
                setStep("already-registered");
                return;
            }
            const msg =
                data?.error === "already_exists"
                    ? "An account with this email already exists."
                    : data?.message || data?.detail || data?.error || "Registration failed. Please try again.";
            setError(msg);
        } finally {
            setIsPending(false);
        }
    };

    const handleVerifyCode = async (e: React.FormEvent) => {
        e.preventDefault();
        setIsPending(true);
        setError(null);
        try {
            const data = await authService.agencyVerifyCode(formData.email, verificationCode);
            if (data.status === "verified") {
                if (data.user) setUser(data.user);
                navigate("/partner");
            } else {
                setError(data.message || "Invalid verification code.");
            }
        } catch (err: any) {
            const data = err.response?.data;
            if (err.response?.status === 429) {
                setError(null);
                setThrottleCooldown(parseThrottleSeconds(err.response?.data?.detail));
            } else {
                setError(
                    data?.error === "invalid_code"
                        ? "Incorrect code. Please check your email and try again."
                        : data?.message || data?.detail || "Failed to verify code."
                );
            }
        } finally {
            setIsPending(false);
        }
    };

    const handleResendCode = async () => {
        setIsResending(true);
        setError(null);
        try {
            await authService.agencyResendVerificationCode(formData.email);
            setTimerKey((p) => p + 1);
            setVerificationCode("");
        } catch (err: any) {
            const data = err.response?.data;
            setError(
                data?.error === "already_verified"
                    ? "Your email is already verified. Please log in."
                    : "Failed to resend code. Please try again."
            );
        } finally {
            setIsResending(false);
        }
    };

    if (step === "already-registered") {
        return (
            <div className="min-h-screen flex items-center justify-center bg-slate-50/50 p-4">
                <div className="w-full max-w-md">
                    <Card className="border-none shadow-2xl bg-white/80 backdrop-blur-sm">
                        <CardContent className="p-8 text-center space-y-6">
                            <div
                                className="h-16 w-16 rounded-full flex items-center justify-center mx-auto"
                                style={{ backgroundColor: `${primaryColor}20`, color: primaryColor }}
                            >
                                <AlertCircle size={32} />
                            </div>
                            <div className="space-y-2">
                                <h2 className="text-2xl font-serif font-bold text-slate-900">Already Registered</h2>
                                <p className="text-sm text-muted-foreground">
                                    An agency account with{" "}
                                    <span className="font-bold text-slate-900">{formData.email}</span> already exists.
                                </p>
                            </div>
                            <div className="space-y-3">
                                <Button
                                    className="w-full h-11 text-white"
                                    style={{ backgroundColor: primaryColor }}
                                    onClick={() => navigate("/partner/agency-login")}
                                >
                                    Go to Login
                                </Button>
                                <Button
                                    variant="outline"
                                    className="w-full h-11"
                                    onClick={() => { setStep("form"); setError(null); set("email", ""); }}
                                >
                                    Try Different Email
                                </Button>
                            </div>
                        </CardContent>
                    </Card>
                </div>
            </div>
        );
    }

    if (step === "verification-sent") {
        return (
            <div className="min-h-screen flex items-center justify-center bg-slate-50/50 p-4">
                <div className="w-full max-w-md">
                    <Card className="border-none shadow-2xl bg-white/80 backdrop-blur-sm">
                        <CardContent className="p-8 text-center space-y-6">
                            <div className="flex justify-center">
                                <div className="h-16 w-16 bg-green-50 text-green-600 rounded-full flex items-center justify-center">
                                    <CheckCircle2 size={32} />
                                </div>
                            </div>
                            <div className="space-y-2">
                                <h2 className="text-2xl font-serif font-bold text-slate-900">Check your email</h2>
                                <p className="text-sm text-muted-foreground">
                                    We've sent a verification code to{" "}
                                    <span className="font-bold text-slate-900">{formData.email}</span>
                                </p>
                            </div>
                            <form onSubmit={handleVerifyCode} className="space-y-4 text-left">
                                <div className="space-y-2">
                                    <Label htmlFor="verification-code">Verification Code</Label>
                                    <Input
                                        id="verification-code"
                                        type="text"
                                        placeholder="Enter your 6-character code"
                                        className={`h-11 bg-white text-center tracking-widest text-lg font-mono uppercase placeholder:text-xs placeholder:tracking-normal placeholder:font-sans placeholder:normal-case ${error && throttleCooldown <= 0 ? "border-red-500" : ""}`}
                                        maxLength={6}
                                        autoComplete="one-time-code"
                                        value={verificationCode}
                                        onChange={(e) => {
                                            setVerificationCode(e.target.value.replace(/[^A-Za-z0-9]/g, "").toUpperCase());
                                            setError(null);
                                        }}
                                    />
                                    {throttleCooldown > 0 ? (
                                        <div className="text-sm text-amber-700 bg-amber-50 border border-amber-200 p-3 rounded-lg flex items-center justify-between mt-2">
                                            <span>Too many attempts. Try again in</span>
                                            <span className="font-mono font-bold">
                                                {String(Math.floor(throttleCooldown / 60)).padStart(2, "0")}:{String(throttleCooldown % 60).padStart(2, "0")}
                                            </span>
                                        </div>
                                    ) : error ? (
                                        <p className="text-red-500 text-sm mt-1">{error}</p>
                                    ) : null}
                                </div>
                                <Button
                                    type="submit"
                                    className="w-full text-white font-bold h-12 rounded-xl shadow-lg"
                                    style={{ backgroundColor: primaryColor }}
                                    disabled={isPending || verificationCode.length !== 6 || throttleCooldown > 0}
                                >
                                    {isPending ? (
                                        <><Loader2 className="mr-2 h-4 w-4 animate-spin" />Verifying...</>
                                    ) : (
                                        "Verify Code"
                                    )}
                                </Button>
                            </form>
                            <div className="pt-4 border-t border-slate-200">
                                {!canResend ? (
                                    <p className="text-sm text-muted-foreground">
                                        Code expires in{" "}
                                        <span className="font-bold text-slate-900">
                                            {String(Math.floor(timeLeft / 60)).padStart(2, "0")}:{String(timeLeft % 60).padStart(2, "0")}
                                        </span>
                                    </p>
                                ) : (
                                    <div className="space-y-2">
                                        <p className="text-sm text-amber-600">Code expired</p>
                                        <Button
                                            variant="outline"
                                            className="w-full"
                                            onClick={handleResendCode}
                                            disabled={isResending}
                                        >
                                            {isResending ? (
                                                <><Loader2 className="mr-2 h-4 w-4 animate-spin" />Sending...</>
                                            ) : (
                                                "Resend Code"
                                            )}
                                        </Button>
                                    </div>
                                )}
                            </div>
                        </CardContent>
                    </Card>
                </div>
            </div>
        );
    }

    return (
        <div className="min-h-screen flex items-center justify-center bg-slate-50/50 p-4 font-sans text-foreground">
            <div className="absolute inset-0 z-0 overflow-hidden pointer-events-none">
                <div className="absolute -top-[10%] -left-[10%] w-[40%] h-[40%] bg-primary/5 rounded-full blur-3xl" />
                <div className="absolute -bottom-[10%] -right-[10%] w-[40%] h-[40%] bg-secondary/10 rounded-full blur-3xl" />
            </div>

            <div className="w-full max-w-lg relative z-10 py-8">
                <div className="flex flex-col items-center mb-8">
                    <Link to="/" className="flex flex-col items-center gap-3 mb-4 text-center">
                        <div className="w-16 h-16 flex items-center justify-center">
                            {!loading && branding?.branding?.logo ? (
                                <img src={branding.branding.logo} alt={`${branding.name} Logo`} className="max-w-full max-h-full object-contain" />
                            ) : (
                                <div className="w-full h-full rounded-lg flex items-center justify-center text-white" style={{ backgroundColor: primaryColor }}>
                                    <GraduationCap className="w-10 h-10" />
                                </div>
                            )}
                        </div>
                        <span className="font-serif text-2xl font-black text-primary text-center" style={{ fontFamily: "var(--font-university-name)" }}>
                            {!loading && branding?.name ? branding.name : "University Portal"}
                        </span>
                    </Link>
                    <h1 className="text-xl font-bold text-slate-900">Become a Partner</h1>
                    <p className="text-sm text-muted-foreground mt-1">Register your agency to get started</p>
                </div>

                <Card className="border-none shadow-2xl overflow-hidden bg-white/80 backdrop-blur-sm">
                    <CardContent className="p-8">
                        <form onSubmit={handleSubmit} className="space-y-5" noValidate>
                            {/* Company Information */}
                            <div>
                                <h3 className="text-sm font-black text-slate-700 uppercase tracking-wider mb-3">Company Information</h3>
                                <div className="space-y-3">
                                    <div className="space-y-1.5">
                                        <Label htmlFor="company_name">Company Name <span className="text-red-500">*</span></Label>
                                        <Input
                                            id="company_name"
                                            placeholder="Acme Agency Ltd."
                                            className={`h-11 bg-white ${fieldErrors.company_name ? "border-red-500 focus-visible:ring-red-500" : ""}`}
                                            value={formData.company_name}
                                            onChange={(e) => { set("company_name", e.target.value); setFieldErrors((prev) => ({ ...prev, company_name: null })); }}
                                            maxLength={255}
                                            ref={companyNameRef}
                                        />
                                        {fieldErrors.company_name && <p className="text-xs text-red-500">{fieldErrors.company_name}</p>}
                                    </div>
                                    <div className="space-y-1.5">
                                        <Label htmlFor="tax_number">Company Tax Number</Label>
                                        <Input id="tax_number" placeholder="Tax / VAT number" className="h-11 bg-white" value={formData.tax_number} onChange={(e) => set("tax_number", e.target.value)} />
                                    </div>
                                    <div className="space-y-1.5">
                                        <Label htmlFor="website">Website</Label>
                                        <Input id="website" placeholder="https://example.com" className="h-11 bg-white" value={formData.website} onChange={(e) => set("website", e.target.value)} />
                                    </div>
                                </div>
                            </div>

                            {/* Address Information */}
                            <div>
                                <h3 className="text-sm font-black text-slate-700 uppercase tracking-wider mb-3">Address Information</h3>
                                <div className="space-y-3">
                                    <SearchablePicklist
                                        id="address_country"
                                        label="Address: Country"
                                        options={countries}
                                        value={formData.address_country}
                                        onChange={(v) => set("address_country", v)}
                                        placeholder="Search Country..."
                                        searchPlaceholder="Search country..."
                                    />
                                    <div className="space-y-1.5">
                                        <Label htmlFor="address_city">Address: City</Label>
                                        <Input id="address_city" placeholder="City" className="h-11 bg-white" value={formData.address_city} onChange={(e) => set("address_city", e.target.value)} />
                                    </div>
                                    <div className="space-y-1.5">
                                        <Label htmlFor="address_street">Address: Street</Label>
                                        <Input id="address_street" placeholder="Street address" className="h-11 bg-white" value={formData.address_street} onChange={(e) => set("address_street", e.target.value)} />
                                    </div>
                                    <div className="space-y-1.5">
                                        <Label htmlFor="address_zip">Address: Zip/Code</Label>
                                        <Input id="address_zip" placeholder="Postal code" className="h-11 bg-white" value={formData.address_zip} onChange={(e) => set("address_zip", e.target.value)} />
                                    </div>
                                </div>
                            </div>

                            {/* Primary Contact Information */}
                            <div>
                                <h3 className="text-sm font-black text-slate-700 uppercase tracking-wider mb-3">Primary Contact Information</h3>
                                <div className="space-y-3">
                                    <div className="grid grid-cols-2 gap-3">
                                        <div className="space-y-1.5">
                                            <Label htmlFor="first_name">First Name <span className="text-red-500">*</span></Label>
                                            <Input
                                                id="first_name"
                                                placeholder="James"
                                                className={`h-11 bg-white ${fieldErrors.first_name ? "border-red-500 focus-visible:ring-red-500" : ""}`}
                                                value={formData.first_name}
                                                onChange={(e) => handleNameChange("first_name", e.target.value)}
                                                maxLength={40}
                                                ref={firstNameRef}
                                            />
                                            {fieldErrors.first_name && <p className="text-xs text-red-500">{fieldErrors.first_name}</p>}
                                        </div>
                                        <div className="space-y-1.5">
                                            <Label htmlFor="last_name">Last Name <span className="text-red-500">*</span></Label>
                                            <Input
                                                id="last_name"
                                                placeholder="Smith"
                                                className={`h-11 bg-white ${fieldErrors.last_name ? "border-red-500 focus-visible:ring-red-500" : ""}`}
                                                value={formData.last_name}
                                                onChange={(e) => handleNameChange("last_name", e.target.value)}
                                                maxLength={40}
                                                ref={lastNameRef}
                                            />
                                            {fieldErrors.last_name && <p className="text-xs text-red-500">{fieldErrors.last_name}</p>}
                                        </div>
                                    </div>

                                    <div className="space-y-1.5">
                                        <Label htmlFor="preferred_contact_method">Preferred Contact Method</Label>
                                        <select
                                            id="preferred_contact_method"
                                            className="w-full h-11 rounded-xl border border-slate-800 bg-white px-3 text-sm text-slate-900 focus:outline-none focus:ring-2 focus:ring-ring"
                                            value={formData.preferred_contact_method}
                                            onChange={(e) => set("preferred_contact_method", e.target.value)}
                                        >
                                            <option value="">--None--</option>
                                            {CONTACT_METHODS.map((m) => (
                                                <option key={m} value={m}>{m}</option>
                                            ))}
                                        </select>
                                    </div>

                                    <div className="space-y-1.5">
                                        <Label htmlFor="phone_number">Phone <span className="text-red-500">*</span></Label>
                                        <div className="flex gap-2">
                                            <PhoneCodePicker value={formData.phone_code} onChange={(v) => set("phone_code", v)} />
                                            <Input
                                                id="phone_number"
                                                type="tel"
                                                placeholder="5550000000"
                                                maxLength={15}
                                                className={`h-12 bg-white flex-1 ${fieldErrors.phone_number ? "border-red-500 focus-visible:ring-red-500" : ""}`}
                                                value={formData.phone_number}
                                                onChange={(e) => { set("phone_number", e.target.value.replace(/[^0-9]/g, "")); setFieldErrors((prev) => ({ ...prev, phone_number: null })); }}
                                                ref={phoneNumberRef}
                                            />
                                        </div>
                                        {fieldErrors.phone_number && <p className="text-xs text-red-500">{fieldErrors.phone_number}</p>}
                                    </div>

                                    <div className="space-y-1.5">
                                        <Label htmlFor="email">Username (Email) <span className="text-red-500">*</span></Label>
                                        <Input
                                            id="email"
                                            type="email"
                                            placeholder="your.email@example.com"
                                            className={`h-11 bg-white ${fieldErrors.email ? "border-red-500 focus-visible:ring-red-500" : ""}`}
                                            value={formData.email}
                                            onChange={(e) => { set("email", e.target.value); setFieldErrors((prev) => ({ ...prev, email: null })); }}
                                            ref={emailRef}
                                        />
                                        {fieldErrors.email && <p className="text-xs text-red-500">{fieldErrors.email}</p>}
                                    </div>

                                    <div className="space-y-1.5">
                                        <Label htmlFor="password">Password <span className="text-red-500">*</span></Label>
                                        <Input
                                            id="password"
                                            type="password"
                                            placeholder="••••••••"
                                            className={`h-11 bg-white ${fieldErrors.password ? "border-red-500 focus-visible:ring-red-500" : ""}`}
                                            value={formData.password}
                                            onChange={(e) => { set("password", e.target.value); setFieldErrors((prev) => ({ ...prev, password: null, confirm_password: null })); }}
                                            ref={passwordRef}
                                        />
                                        {fieldErrors.password ? (
                                            <p className="text-xs text-red-500">{fieldErrors.password}</p>
                                        ) : (
                                            <p className="text-xs text-slate-500">Password must be at least 8 characters</p>
                                        )}
                                    </div>

                                    <div className="space-y-1.5">
                                        <Label htmlFor="confirm_password">Confirm Password <span className="text-red-500">*</span></Label>
                                        <Input
                                            id="confirm_password"
                                            type="password"
                                            placeholder="••••••••"
                                            className={`h-11 bg-white ${fieldErrors.confirm_password ? "border-red-500 focus-visible:ring-red-500" : ""}`}
                                            value={formData.confirm_password}
                                            onChange={(e) => { set("confirm_password", e.target.value); setFieldErrors((prev) => ({ ...prev, confirm_password: null })); }}
                                            ref={confirmPasswordRef}
                                        />
                                        {fieldErrors.confirm_password && (
                                            <p className="text-xs text-red-500">{fieldErrors.confirm_password}</p>
                                        )}
                                    </div>
                                </div>
                            </div>

                            {error && (
                                <div className="rounded-lg bg-red-50 border border-red-200 p-3">
                                    <p className="text-sm text-red-800">{error}</p>
                                </div>
                            )}

                            <Button
                                type="submit"
                                className="w-full text-white font-bold h-12 rounded-xl shadow-lg mt-2"
                                style={{ backgroundColor: primaryColor }}
                                disabled={isPending}
                            >
                                {isPending ? <Loader2 className="h-5 w-5 animate-spin" /> : "Register"}
                            </Button>
                        </form>
                    </CardContent>
                    <CardFooter className="flex justify-center border-t border-slate-400 bg-slate-50/50 py-6">
                        <p className="text-sm text-slate-500">
                            Already have an account?{" "}
                            <Link to="/partner/agency-login" className="font-semibold hover:underline" style={{ color: primaryColor }}>
                                Log in here
                            </Link>
                        </p>
                    </CardFooter>
                </Card>
            </div>
        </div>
    );
};

export default AgencyRegister;
